import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { OfficeIcon, HospitalIcon } from '../icons/XionIcons';
import { systemComponents } from '../../data/mock';

const SystemCard = ({ system, index }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ delay: index * 0.1 }}
    data-testid={`home-system-card-${system.id}`}
    className="card-dark corner-brackets p-6 group"
  >
    <Link to={`/system-solutions/${system.id}`} className="block" data-testid={`home-system-link-${system.id}`}>
      <h4 className="text-xl font-semibold text-white mb-3 group-hover:text-[#00FFD1] transition-colors">
        {system.name}
      </h4>
      <p className="text-white/60 text-sm mb-4 leading-relaxed">
        {system.description}
      </p>
      <div className="flex flex-wrap gap-2">
        {system.features.map((feature, i) => (
          <span
            key={i}
            className="text-xs font-mono px-2 py-1 bg-white/5 border border-white/10 text-white/70"
          >
            {feature}
          </span>
        ))}
      </div>
      <div className="mt-5 flex items-center gap-2 text-[#00FFD1] text-sm font-mono opacity-70 group-hover:opacity-100 transition-opacity">
        View system
        <ArrowRight size={14} />
      </div>
    </Link>
  </motion.div>
);

const SystemsSection = () => {
  return (
    <section className="section bg-[#0a0a0a]" aria-labelledby="systems-heading">
      <div className="max-w-7xl mx-auto px-6">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <p className="terminal-label text-[#00FFD1] mb-4">// System Components</p>
          <h2 id="systems-heading" className="text-4xl md:text-5xl font-bold text-white mb-4">
            Complete Endoscopy Solutions
          </h2>
          <p className="text-white/60 max-w-2xl mx-auto">
            From doctor&apos;s offices to hospital operating rooms, XION delivers comprehensive
            endoscopy systems tailored to your clinical environment.
          </p>
        </motion.div>

        {/* Complete systems line-up */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="relative mb-16"
          data-testid="systems-lineup-banner"
        >
          <div className="absolute inset-x-0 bottom-6 h-24 bg-[#00FFD1]/10 blur-3xl rounded-full" aria-hidden />
          <img
            src="/assets/xion-systems-lineup.png"
            alt="The complete XION endoscopy system line-up"
            className="relative z-10 w-full max-w-5xl mx-auto object-contain drop-shadow-[0_24px_60px_rgba(0,0,0,0.6)]"
          />
        </motion.div>


        {/* Office & Clinic Systems */}
        <div className="mb-16">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="flex items-center gap-4 mb-8"
          >
            <div className="w-12 h-12 border border-[#00FFD1]/30 flex items-center justify-center">
              <OfficeIcon className="text-[#00FFD1]" size={24} />
            </div>
            <div>
              <h3 className="text-2xl font-semibold text-white">
                {systemComponents.officeClinic.title}
              </h3>
              <p className="text-white/60 text-sm">
                {systemComponents.officeClinic.description}
              </p>
            </div>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-6">
            {systemComponents.officeClinic.systems.map((system, index) => (
              <SystemCard key={system.id} system={system} index={index} />
            ))}
          </div>
        </div>

        {/* Hospital Systems */}
        <div className="mb-12">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="flex items-center gap-4 mb-8"
          >
            <div className="w-12 h-12 border border-[#00FFD1]/30 flex items-center justify-center">
              <HospitalIcon className="text-[#00FFD1]" size={24} />
            </div>
            <div>
              <h3 className="text-2xl font-semibold text-white">
                {systemComponents.hospital.title}
              </h3>
              <p className="text-white/60 text-sm">
                {systemComponents.hospital.description}
              </p>
            </div>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-6">
            {systemComponents.hospital.systems.map((system, index) => (
              <SystemCard key={system.id} system={system} index={index + 2} />
            ))}
          </div>
        </div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center"
        >
          <Link to="/system-solutions" className="btn-primary">
            View All System Solutions
            <ArrowRight size={16} />
          </Link>
        </motion.div>
      </div>
    </section>
  );
};

export default SystemsSection;
